import { useState, type FormEvent } from "react";
import PageMeta from "@/components/feature/PageMeta";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { contactChannels } from "@/mocks/site";

const SITE = "https://son4min.cn";
const FORM_ENDPOINT = "https://readdy.ai/api/form/dahfsabp14a1h3mr6l7g";

const ISSUE_TYPES = [
  "平台疑似跑路 / 停止运营",
  "付款后不发货或服务未提供",
  "退款拖延或拒绝退款",
  "高息诱导 / 疑似资金盘",
  "兼职刷单 / 垫付返现",
  "贷款费用不透明 / 催收问题",
  "预付卡余额无法使用",
  "信息更正或下架申请",
  "其他情况",
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const [detailLength, setDetailLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("提交成功，感谢你的反馈，我们会在核实后处理。");
      form.reset();
      setDetailLength(0);
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFeedback("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("提交成功，感谢你的反馈，我们会在 24 小时内完成初步核实。");
        form.reset();
        setDetailLength(0);
      } else {
        setStatus("error");
        setFeedback(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后重试，或通过邮箱与我们联系。",
        );
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交失败，请稍后重试，或通过邮箱与我们联系。");
    }
  };

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系我们与提交线索",
      url: `${SITE}/contact`,
      inLanguage: "zh-CN",
      description: "提交网站黑料线索、补充材料或申请信息更正，也可通过邮箱与避雷哨取得联系。",
      mainEntity: {
        "@type": "Organization",
        name: "避雷哨",
        url: `${SITE}/`,
        email: "tips@bileishao.example",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "联系与提交线索", item: `${SITE}/contact` },
      ],
    },
  ];

  const inputClass =
    "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 font-label text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400";

  return (
    <>
      <PageMeta
        title="联系我们_提交网站黑料线索与更正申请 - 避雷哨"
        description="提交网站黑料线索、补充材料或申请信息更正。填写平台名称、问题类型与经过描述，我们会在 24 小时内完成初步核实。"
        keywords="提交黑料,网站黑料线索,信息更正,联系我们"
        path="/contact"
        jsonLd={jsonLd}
      />

      <section className="border-b border-background-200 bg-gradient-to-b from-background-100 to-background-50">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ name: "联系与提交线索" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            提交线索，或者告诉我们哪里写错了
          </h1>
          <p className="mt-3 max-w-3xl font-body text-sm leading-relaxed text-foreground-600 md:text-base">
            信息越完整，核实越快。请尽量写清平台名称、问题类型与发生经过，并提供可佐证的截图或凭证。你的联系信息仅用于核实，不会公开展示。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.35fr] lg:gap-12">
          <div className="flex flex-col gap-5">
            <h2 className="font-heading text-xl font-bold text-foreground-950">先看看这几种联系方式</h2>
            <div className="flex flex-col gap-4">
              {contactChannels.map((channel) => (
                <div key={channel.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                    <i className={`${channel.icon} text-xl`} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-heading text-base font-bold text-foreground-950">{channel.title}</h3>
                    <p className="mt-1 break-all font-label text-sm font-medium text-primary-700">{channel.value}</p>
                    <p className="mt-1.5 font-label text-xs leading-relaxed text-foreground-500">{channel.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-950">
                <i className="ri-shield-check-line text-secondary-600" />
                提交前的小提醒
              </h3>
              <ul className="mt-3 space-y-2.5">
                {[
                  "请勿提交包含他人隐私的信息",
                  "描述尽量按时间顺序写，标注关键金额",
                  "截图可后续通过邮件补充",
                  "恶意或虚假举报将被记录并拒绝处理",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 font-label text-sm text-secondary-900">
                    <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">线索提交表单</h2>
            <p className="mt-2 font-label text-sm text-foreground-500">带 * 的为必填项</p>

            <form
              data-readdy-form
              id="tips-submit-form"
              onSubmit={handleSubmit}
              className="mt-6 flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block font-label text-sm font-medium text-foreground-800">
                    称呼 *
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    placeholder="怎么称呼你"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block font-label text-sm font-medium text-foreground-800">
                    联系邮箱 *
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    placeholder="用于核实与回复"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-platform" className="mb-1.5 block font-label text-sm font-medium text-foreground-800">
                    平台名称或域名 *
                  </label>
                  <input
                    id="contact-platform"
                    type="text"
                    name="platform"
                    required
                    placeholder="例如：某某商城 / xxx.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-issue" className="mb-1.5 block font-label text-sm font-medium text-foreground-800">
                    问题类型 *
                  </label>
                  <select id="contact-issue" name="issue_type" required defaultValue="" className={`${inputClass} cursor-pointer`}>
                    <option value="" disabled>
                      请选择问题类型
                    </option>
                    {ISSUE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-date" className="mb-1.5 block font-label text-sm font-medium text-foreground-800">
                    发生时间
                  </label>
                  <input id="contact-date" type="date" name="occurred_at" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="contact-amount" className="mb-1.5 block font-label text-sm font-medium text-foreground-800">
                    涉及金额（元）
                  </label>
                  <input
                    id="contact-amount"
                    type="number"
                    name="amount"
                    min="0"
                    placeholder="选填，便于判断风险程度"
                    className={inputClass}
                  />
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="contact-detail" className="block font-label text-sm font-medium text-foreground-800">
                    情况描述 *
                  </label>
                  <span className="font-label text-xs text-foreground-400">{detailLength}/500</span>
                </div>
                <textarea
                  id="contact-detail"
                  name="detail"
                  required
                  maxLength={500}
                  rows={5}
                  onChange={(event) => setDetailLength(event.target.value.length)}
                  placeholder="请按时间顺序描述经过，例如：何时付款、购买了什么、平台如何回应，现在处于什么状态。"
                  className={`${inputClass} resize-y leading-relaxed`}
                />
              </div>

              <input
                type="text"
                name="website_alt"
                className="form-anti-spam-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="flex flex-col gap-4 border-t border-background-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="font-label text-xs leading-relaxed text-foreground-500 sm:max-w-xs">
                  提交即表示你确认所填信息真实，并同意我们用于核实与整理收录。
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base" />
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-base" />
                      提交线索
                    </>
                  )}
                </button>
              </div>

              {status === "success" && (
                <div
                  role="status"
                  className="flex items-start gap-2.5 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3.5"
                >
                  <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" />
                  <p className="font-label text-sm text-secondary-900">{feedback}</p>
                </div>
              )}

              {status === "error" && (
                <div
                  role="alert"
                  className="flex items-start gap-2.5 rounded-md border border-accent-200 bg-accent-50 px-4 py-3.5"
                >
                  <i className="ri-error-warning-line mt-0.5 text-accent-600" />
                  <p className="font-label text-sm text-accent-900">{feedback}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}